import CodiceFiscale from 'codice-fiscale-js';
import { Location } from './Location';
import { isValid, parseISO } from 'date-fns';

// Historical cadastral codes mapping (municipalities that changed codes)
const HISTORICAL_CADASTRAL_CODES: Record<string, string> = {
    'A744': 'M335', // Bellagio
    'B508': 'M373', // Campiglia Cervo
    'C005': 'M388', // Cassano Spinola
    'E552': 'M371', // Lessona
    'E672': 'M342', // Longarone
    'F402': 'M378', // Montalcino
    'I349': 'M333', // Sant'Omobono Terme
    'I879': 'M436', // Sovizzo
};

/**
 * Define User  of the SPB portal (it can be a individual or company client)
 */
export class User {
    id: string;
    name?: string;
    lastName?: string;
    CF?: string;
    businessName?: string;
    PIVA?: string;
    partnerId: string;
    birthdate: string;
    location: Location;
    password?: string;
    email: string;
    userType: string;
    insuredType?: string;
    phone: string;
    ToC: boolean;
    isImpersonated: boolean;
    notes?: string;
    isDeliveryToPickupPoint?: boolean;
    partnerName?: string;
    sessid?: string;
    optionalFlags: {
        NPS?: boolean;
        MKT?: boolean;
    };

    deleted: boolean;
    pickupDate: string | null;

    constructor(user?: any) {
        this.id = user?.id;
        this.name = user?.name;
        this.lastName = user?.lastName;
        this.CF = user?.CF;
        this.businessName = user?.businessName ?? '';
        this.PIVA = user?.PIVA ?? '';
        this.partnerId = user?.partnerId ?? '';
        this.birthdate = user?.birthdate ? new Date(user?.birthdate).toISOString() : '1970-01-01';
        this.location = new Location(user?.location);
        this.email = user?.email ?? '';
        this.userType = user?.userType ?? 'user';
        this.insuredType = user?.userType ?? 'user';
        this.phone = user?.phone ?? '';
        this.ToC = user?.ToC ?? false;
        this.isImpersonated = user?.isImpersonated ?? false;
        this.notes = '';
        this.pickupDate = null;
        this.optionalFlags = user?.optionalFlags ?? {};
        this.deleted = user?.deleted ?? false;
    }

    validate(checkComplete: boolean = false, skipProfileFields = false) {
        let errors: { [key: string]: boolean } = {};
        let valid: boolean = true;

        if (!this.lastName || this.lastName.trim() === '' || this.lastName.length > 30) {
            valid = false;
            errors['lastName'] = true;
        }
        if (!this.name || this.name.trim() === '' || this.name.length > 30) {
            valid = false;
            errors['name'] = true;
        }

        if (
            this.userType === 'business' &&
            (!this.businessName ||
                this.businessName.trim() === '' ||
                this.businessName.length > 50)
        ) {
            valid = false;
            errors['businessName'] = true;
        }
        if (
            this.userType === 'business' &&
            !skipProfileFields &&
            (!this.PIVA || this.PIVA.trim() === '')
        ) {
            valid = false;
            errors['PIVA'] = true;
        }


        if (!this.location.completeAddress || this.location.completeAddress.trim() === '' || this.location.completeAddress.length > 50) {
            valid = false;
            errors['address'] = true;
        }

        if (
            !this.location.zipCode ||
            this.location.zipCode.trim() === '' ||
            this.location.zipCode.length > 5
        ) {
            valid = false;
            errors['zipCode'] = true;
        }

        if (
            !this.location.city ||
            this.location.city.trim() === '' ||
            this.location.city.length > 30
        ) {
            valid = false;
            errors['city'] = true;
        }

        if (
            !this.location.state ||
            this.location.state.trim() === '' ||
            this.location.state.length > 2
        ) {
            valid = false;
            errors['state'] = true;
        }

        if (!this.phone || this.phone.trim() === '' || this.phone.length > 20) {
            valid = false;
            errors['phone'] = true;
        }

        if (
            !this.email ||
            this.email.trim() === '' ||
            this.email.length > 50 ||
            !this.isValidEmail(this.email)
        ) {
            valid = false;
            errors['email'] = true;
        }

        if (!this.birthdate || new Date(this.birthdate) < new Date('1900-01-01')) {
            valid = false;
            errors['birthdate'] = true;
        }

        if (
            this.userType !== 'business' &&
            !skipProfileFields &&
            (!this.CF || !this.checkFiscalCode())
        ) {
            valid = false;
            errors['fiscalCode'] = true;
        }

        if (checkComplete) {
            if (this.password) {
                if (this.password?.length < 10) {
                    valid = false;
                    errors['password'] = true;
                }

                if (!/[A-Z]/.test(this.password)) {
                    valid = false;
                    errors['password'] = true;
                }

                if (!/[a-z]/.test(this.password)) {
                    valid = false;
                    errors['password'] = true;
                }

                if (!/\d/.test(this.password)) {
                    valid = false;
                    errors['password'] = true;
                }

                if (!/[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(this.password)) {
                    valid = false;
                    errors['password'] = true;
                }
            }

            if (!this.ToC) {
                valid = false;
                errors['ToC'] = true;
            }
        }

        return {
            errors: errors,
            isValid: valid,
        };
    }

    // Translate CF with historical cadastral code to current code
    private translateHistoricalCF(cf: string): string {
        const cadastralCode = cf.substring(11, 15);
        const newCode = HISTORICAL_CADASTRAL_CODES[cadastralCode];
        if (!newCode) return cf;

        // Replace cadastral code and recalculate checksum
        const cfWithNewCode = cf.substring(0, 11) + newCode;
        const checksum = CodiceFiscale.getCheckCode(cfWithNewCode);
        return cfWithNewCode + checksum;
    }

    checkFiscalCode() {
        try {
            // Translate historical cadastral codes to current ones
            const translatedCF = this.translateHistoricalCF(this.CF ?? '');

            const birthdate = new Date(this.birthdate);
            const invertedCF = new CodiceFiscale(translatedCF);
            const invertedFields = invertedCF.toJSON();
            const computedCF = new CodiceFiscale({
                name: this.name ?? '',
                surname: this.lastName ?? '',
                gender: invertedFields.gender,
                birthplace: invertedFields.birthplace,
                birthplaceProvincia: invertedFields.birthplaceProvincia,
                day: birthdate.getDate(),
                month: birthdate.getMonth() + 1,
                year: birthdate.getFullYear(),
            });

            return computedCF.toString() === translatedCF;
        } catch (error) {
            return false;
        }
    }
    isAdult() {
        if (!this.birthdate) return false;
        return this.getAge() >= 18;
    }

    getAge() {
        const birthDate = new Date(this.birthdate);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const month = today.getMonth() - birthDate.getMonth();
        if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }

    private isValidEmail(email: string): boolean {
        const emailRegex = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
        if (!email) return false;

        const emailParts = email.split('@');

        if (emailParts.length !== 2) return false;

        const account = emailParts[0];
        const address = emailParts[1];

        if (account.length > 64) return false;
        else if (address.length > 255) return false;

        const domainParts = address.split('.');

        if (domainParts.some((part) => part.length > 63)) return false;

        return emailRegex.test(email);
    }
}
